import {
  IconBell,
  IconChevronRight,
  IconPhoto,
  IconSpeakerphone,
} from '@tabler/icons-react';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { InboxVariant } from './inbox-variant';

const SAMPLE_PLACEHOLDERS: Record<string, string> = {
  first_name: 'Customer',
  surname: 'Surname',
  brand: 'Selected brand',
};

function renderSample(text: string, fallback: string): string {
  const rendered = text.replace(
    /{{\s*(first_name|surname|brand)\s*}}/g,
    (_, key: string) => SAMPLE_PLACEHOLDERS[key] ?? '',
  );
  return rendered.trim() || fallback;
}

function InboxMediaPreview({ value }: { value: InboxVariant }) {
  const videoUrl = value.videoUrl.trim();
  const imageUrl = value.imageUrl.trim();

  if (videoUrl.startsWith('https://')) {
    return (
      <video
        className="aspect-video w-full bg-muted object-cover"
        controls
        muted
        preload="metadata"
        src={videoUrl}
      >
        Your browser does not support video preview.
      </video>
    );
  }

  if (imageUrl.startsWith('https://')) {
    return (
      <img
        className="aspect-video w-full bg-muted object-cover"
        src={imageUrl}
        alt="Campaign media preview"
      />
    );
  }

  return (
    <div className="flex aspect-video w-full flex-col items-center justify-center gap-2 bg-muted text-muted-foreground">
      <IconPhoto className="size-8" aria-hidden="true" />
      <span className="text-xs">No campaign media selected</span>
    </div>
  );
}

function MessageListPreview({ value }: { value: InboxVariant }) {
  return (
    <Card size="sm">
      <CardContent className="flex items-center gap-3">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
          <IconBell className="size-5" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">
            {renderSample(value.headline, 'Your headline')}
          </p>
          <p className="line-clamp-2 text-sm text-muted-foreground">
            {renderSample(value.preview, 'Your notification preview')}
          </p>
        </div>
        <IconChevronRight
          className="size-5 shrink-0 text-muted-foreground"
          aria-hidden="true"
        />
      </CardContent>
    </Card>
  );
}

function PromotionListPreview({ value }: { value: InboxVariant }) {
  const hasMedia = Boolean(value.imageUrl.trim() || value.videoUrl.trim());

  return (
    <Card className="gap-0 py-0">
      <div className="overflow-hidden">
        {hasMedia ? (
          <InboxMediaPreview value={value} />
        ) : (
          <div className="flex aspect-[2/1] w-full items-center justify-center bg-gradient-to-br from-primary to-primary/70 text-primary-foreground">
            <IconSpeakerphone className="size-10" aria-hidden="true" />
          </div>
        )}
      </div>
      <CardHeader className="py-4">
        <CardTitle>{renderSample(value.headline, 'Your headline')}</CardTitle>
        <CardDescription>
          {renderSample(value.preview, 'Your notification preview')}
        </CardDescription>
      </CardHeader>
      {value.ctaUrl.trim() && (
        <CardContent className="pb-4">
          <div className="flex items-center justify-center gap-1 rounded-md bg-primary/10 px-3 py-2 font-medium text-primary">
            View offer
            <IconChevronRight className="size-4" aria-hidden="true" />
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function OpenedMessagePreview({ value }: { value: InboxVariant }) {
  return (
    <Card>
      <CardHeader>
        <Badge variant="outline" className="w-fit">
          {value.category.toUpperCase()}
        </Badge>
        <CardTitle className="text-lg">
          {renderSample(value.headline, 'Your headline')}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="whitespace-pre-wrap text-sm">
          {renderSample(value.fullMessage, 'Your full message')}
        </p>
        {value.category === 'Promotion' && <InboxMediaPreview value={value} />}
      </CardContent>
    </Card>
  );
}

export function InboxMessagePreview({ value }: { value: InboxVariant }) {
  return (
    <section className="flex flex-col gap-2 border-t border-border pt-4">
      <div className="flex flex-col gap-1">
        <h5 className="text-sm font-semibold">Customer preview</h5>
        <p className="text-xs text-muted-foreground">
          Approximate app presentation. Recipient placeholders use sample values
          here and are personalised when sent.
        </p>
      </div>

      <Tabs defaultValue="list">
        <TabsList className="w-full">
          <TabsTrigger value="list">Inbox list</TabsTrigger>
          <TabsTrigger value="opened">Opened message</TabsTrigger>
        </TabsList>
        <TabsContent value="list">
          {value.category === 'Promotion' ? (
            <PromotionListPreview value={value} />
          ) : (
            <MessageListPreview value={value} />
          )}
        </TabsContent>
        <TabsContent value="opened">
          <OpenedMessagePreview value={value} />
        </TabsContent>
      </Tabs>
    </section>
  );
}
